import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fileUrl } from "../lib/api";
import { StatusBadge } from "./StatusBadge";
import { CaseFilePlaceholder } from "./CaseFilePlaceholder";
import { StreamerBadges } from "./StreamerBadges";
import { InfoTip } from "./InfoTip";
import { PLATFORM_COLORS, videoThumbnail } from "../lib/constants";
import { ChevronUp, ChevronDown, Video, Repeat, Flame, ShieldQuestion, Ban, RefreshCw, Crosshair, Info, Radar, ShieldOff, Users } from "lucide-react";

const TRACKABLE_PLATFORMS = ["Steam", "Xbox", "PS5"];

export const ReportCard = ({ report, onUpvote, onDownvote, onOpen, index = 0 }) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  // Card cover: uploaded screenshot first, else auto-pull the video's thumbnail (YouTube).
  // Demos intentionally skip the video thumbnail and use the clean CASE FILE placeholder.
  const coverSrc = (report.images && report.images[0])
    ? fileUrl(report.images[0])
    : (report.is_demo ? null : videoThumbnail(report.video_links?.[0]));
  const platColor = PLATFORM_COLORS[report.platform] || "#94A3B8";
  const isSuspect = report.status === "suspect" || report.status === "disputed";
  const isConfirmed = report.status === "confirmed_cheater" || report.status === "banned";
  const isDemo = !!report.is_demo;
  const isTracked = !!report.platform_uid;
  const isTrackable = TRACKABLE_PLATFORMS.includes(report.platform);

  return (
    <div
      data-testid={`report-card-${report.id}`}
      onClick={() => onOpen(report)}
      className="group animate-fade-up cursor-pointer overflow-hidden rounded-xl border border-[#1E293F] bg-[#141B2D] transition-all duration-300 hover:-translate-y-1 hover:border-[#00F0FF]/40 hover:shadow-[0_8px_40px_rgba(0,240,255,0.12)]"
      style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
    >
      <div className="relative h-40 overflow-hidden bg-[#0F1424]">
        {coverSrc && !imgError ? (
          <img
            src={coverSrc}
            alt={report.gamertag}
            onError={() => setImgError(true)}
            className="h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <CaseFilePlaceholder report={report} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141B2D] via-transparent to-transparent" />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          <StatusBadge status={report.status} testid={`card-status-${report.id}`} />
          {report.community_ban_confirmed && (
            <span data-testid={`ban-confirmed-badge-${report.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#00FF87] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#062B1A] shadow-[0_0_14px_rgba(0,255,135,0.5)]">
              <Ban size={11} /> Banned · {report.ban_flags} confirmed
            </span>
          )}
          {report.hot && (
            <span data-testid={`hot-badge-${report.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#F97316] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              <Flame size={11} /> Hot Suspect
            </span>
          )}
          {report.ai_summary?.green_streak && !isDemo && (
            <span data-testid={`green-streak-badge-${report.id}`} title="AI flagged an unusually high rate of contested greens — likely macro / auto-green." className="inline-flex items-center gap-1 rounded-full bg-[#FF2A55] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-[0_0_14px_rgba(255,42,85,0.5)]">
              <Flame size={11} /> Green Streak
            </span>
          )}
          {report.runs_with?.length > 0 && (
            <span data-testid={`runs-with-badge-${report.id}`} title={`Runs with: ${report.runs_with.map((t) => t.gamertag).join(", ")} — caught in the same clip`} className="inline-flex items-center gap-1 rounded-full bg-[#7C3AED] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-[0_0_14px_rgba(124,58,237,0.5)]">
              <Users size={11} /> Runs with {report.runs_with.length}
            </span>
          )}
          {report.is_serial && (
            <span data-testid={`serial-badge-${report.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FF2A55] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              <Repeat size={11} /> Serial Offender
            </span>
          )}
          {report.name_changed && (
            <span data-testid={`namechange-badge-${report.id}`} title={`Now goes by "${report.tracked_name}"`} className="inline-flex items-center gap-1 rounded-full bg-[#F97316] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              <RefreshCw size={11} /> Changed Name
            </span>
          )}
          {isConfirmed && !isDemo && isTracked && (
            <span className="inline-flex items-center gap-1">
              <span data-testid={`tracking-badge-${report.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#00FF87] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#062B1A] shadow-[0_0_14px_rgba(0,255,135,0.45)]">
                <Radar size={11} /> Tracking
              </span>
              <InfoTip testid={`tracking-info-${report.id}`} side="bottom" text="This cheater is locked to their permanent platform ID (Steam/Xbox/PSN). Our tracker auto-checks their current name every hour — if they rename to dodge the wall, the case updates automatically." />
            </span>
          )}
          {isConfirmed && !isDemo && !isTracked && isTrackable && (
            <span className="inline-flex items-center gap-1">
              <span data-testid={`not-linked-badge-${report.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#1E293F] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] border border-[#334155]">
                <ShieldOff size={11} /> Not linked
              </span>
              <InfoTip testid={`not-linked-info-${report.id}`} side="bottom" text="No permanent platform ID was captured for this case, so we can't auto-detect if they rename. A mod can still log a name change manually." />
            </span>
          )}
          {isDemo && (
            <span data-testid={`demo-badge-${report.id}`} title="Sample case — for demonstration only" className="inline-flex items-center gap-1 rounded-full bg-[#00F0FF] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#04222A]">
              <Info size={11} /> Demo
            </span>
          )}
        </div>
        {report.video_links?.length > 0 && (
          <div className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-[#00FF87] backdrop-blur">
            <Video size={12} /> Clip
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <button
              data-testid={`card-gamertag-${report.id}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/players/${report.platform}/${encodeURIComponent(report.gamertag)}`); }}
              className="block max-w-full truncate text-left font-heading text-lg font-bold text-white transition-colors hover:text-[#00F0FF]"
            >
              {report.gamertag}
            </button>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <span className="rounded px-1.5 py-0.5 font-mono font-semibold" style={{ color: platColor, backgroundColor: `${platColor}18` }}>
                {report.platform}
              </span>
              <span className="text-[#64748B]">•</span>
              <span className="text-[#94A3B8]">{report.game_mode}</span>
            </div>
            {report.name_changed && report.tracked_name && report.tracked_name !== report.gamertag && (
              <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#F97316]" data-testid={`now-known-as-${report.id}`}>
                <RefreshCw size={11} /> now goes by <span className="text-white">{report.tracked_name}</span>
              </div>
            )}
          </div>

          {isSuspect ? (
            <div className="flex shrink-0 flex-col items-center gap-1">
              <button
                data-testid={`upvote-btn-${report.id}`}
                onClick={(e) => { e.stopPropagation(); if (!report.has_upvoted) onUpvote(report); }}
                disabled={report.has_upvoted}
                title={report.has_upvoted ? "You've already marked this — one vote per person" : "I faced this cheater too"}
                className={`flex w-11 flex-col items-center rounded-lg border px-2 py-1 transition-all ${
                  report.has_upvoted ? "border-[#FF2A55] bg-[#FF2A55]/15 text-[#FF2A55] cursor-default" : "border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:border-[#FF2A55]/50 hover:text-[#FF2A55]"
                }`}
              >
                <ChevronUp size={15} strokeWidth={2.5} />
                <span className="text-xs font-bold font-mono">{report.upvotes}</span>
              </button>
              <button
                data-testid={`downvote-btn-${report.id}`}
                onClick={(e) => { e.stopPropagation(); onDownvote && onDownvote(report); }}
                title="They're legit / dispute this"
                className={`flex w-11 flex-col items-center rounded-lg border px-2 py-1 transition-all ${
                  report.has_downvoted ? "border-[#00FF87] bg-[#00FF87]/15 text-[#00FF87]" : "border-[#1E293F] bg-[#0F1424] text-[#64748B] hover:border-[#00FF87]/50 hover:text-[#00FF87]"
                }`}
              >
                <span className="text-xs font-bold font-mono">{report.downvotes}</span>
                <ChevronDown size={15} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <button
              data-testid={`upvote-btn-${report.id}`}
              onClick={(e) => { e.stopPropagation(); if (!report.has_upvoted) onUpvote(report); }}
              disabled={report.has_upvoted}
              title={report.has_upvoted ? "You've already marked this — one vote per person" : "I faced this cheater"}
              className={`flex shrink-0 flex-col items-center rounded-lg border px-2.5 py-1.5 transition-all ${
                report.has_upvoted ? "border-[#FF2A55] bg-[#FF2A55]/15 text-[#FF2A55] cursor-default" : "border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:border-[#FF2A55]/50 hover:text-[#FF2A55]"
              }`}
            >
              <ChevronUp size={16} strokeWidth={2.5} />
              <span className="text-xs font-bold font-mono">{report.upvotes}</span>
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="inline-block rounded-md border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-2 py-1 text-xs font-semibold text-[#FF2A55]">
            {report.cheat_type}
          </span>
          {report.heat > 0 && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#FFB800]/30 bg-[#FFB800]/10 px-2 py-1 text-xs font-semibold text-[#FFB800]">
              🔥 {report.heat}
            </span>
          )}
        </div>
        <p className="mt-2 line-clamp-2 text-sm text-[#94A3B8]">{report.description}</p>

        {isSuspect && (
          <p className="mt-3 flex items-start gap-1.5 rounded-md border border-[#F97316]/25 bg-[#F97316]/[0.07] px-2.5 py-1.5 text-[11px] leading-snug text-[#F59E0B]" data-testid={`unverified-note-${report.id}`}>
            <ShieldQuestion size={13} className="mt-px shrink-0" />
            Community post on a suspected cheater — not confirmed or vetted by moderators.
          </p>
        )}

        {isConfirmed && (report.reporter_name || report.streamer_platforms?.length > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {report.reporter_name && (
              <button
                data-testid={`busted-by-${report.id}`}
                onClick={(e) => { e.stopPropagation(); if (report.reported_by) navigate(`/hunters/${report.reported_by}`); }}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-[#00FF87] hover:underline"
              >
                <Crosshair size={12} className="shrink-0" /> Busted by <span className="text-white">{report.reporter_name}</span>
              </button>
            )}
            <StreamerBadges platforms={report.streamer_platforms} size={13} />
          </div>
        )}
      </div>
    </div>
  );
};
